/* eslint-disable import/no-named-as-default */
import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  Button,
  CHANNELS,
  Modal,
  ModalBody,
  PaymentInfoModalButton,
  useIsUserLogged
} from '@spotahome/ui-library';
import { trans } from '@spotahome/soyuz/client';
import { Mediator } from '@spotahome/soyuz-mediator';
import {
  useDisplayListingPricing,
  usePricingContext
} from '@spotahome/marketplace-common/src/context/PricingContext';
import OffersLabel from '@spotahome/ui-library/src/tenant/components/OffersLabel';

import {
  EXTERNAL_REDIRECTION_LOGIN_MODAL,
  OPEN_BOOKNOW_DATEPICKER_MOBILE
} from '@spotahome/marketplace-common/src/channels';
import { ListingShape } from '@spotahome/marketplace-common/src/shapes/listing';
import BookNowDatesInput from '@spotahome/marketplace-common/src/components/BookNowDatesInput';
import BookNowPriceTable from '@spotahome/marketplace-common/src/components/BookNowCard/BookNowPriceTable';
import BookNowButton from '@spotahome/marketplace-common/src/components/BookNowButton';
import BookNowPermanentContractLabel from '@spotahome/marketplace-common/src/components/BookNowPermanentContractLabel';
import OptimizeDates from '@spotahome/marketplace-common/src/components/OptimizeDates';
import RentableUnitPrice from '@spotahome/marketplace-common/src/components/RentableUnitPrice';

import ValuePropositionFeatures from '../ValuePropositionFeatures';

import BookNowModalHeader from './components/BookNowModalHeader';

import './BookNowModal.scss';

const triggerRedirectModal = bookingRequestFormUrl => {
  Mediator.publish(EXTERNAL_REDIRECTION_LOGIN_MODAL, {
    url: bookingRequestFormUrl,
    initialAction: 'signup',
    events: {
      open: {
        eventCategory: 'signup/login modal',
        eventAction: 'Book-now-with-dates',
        eventLabel: 'open'
      },
      close: {
        eventCategory: 'signup/login modal',
        eventAction: 'Book-now-with-dates',
        eventLabel: 'close'
      }
    }
  });
};

const BookNowModalPriceInfo = () => {
  const displayPricing = useDisplayListingPricing();
  const { hasDates } = usePricingContext();

  const handleClearDates = () => {
    Mediator.publish(CHANNELS.REFRESH_CALENDAR_DATEPICKER);
  };

  return (
    <span className="book-now-modal__info">
      {!hasDates && (
        <span>
          <div className="book-now-modal__message">
            {trans('property_show.book_now_card.select_dates')}
          </div>
        </span>
      )}
      {hasDates && displayPricing?.discount && (
        <OffersLabel appliedOfferValue={displayPricing.discount} offers={[]} />
      )}
      {hasDates && (
        <>
          <RentableUnitPrice showPrice={hasDates} isSmallerRate />
          <Button
            onClick={handleClearDates}
            color="link"
            data-test={`listing-dates-clear`}
          >
            {trans('search.exposedfilters.dates.clear')}
          </Button>
        </>
      )}
    </span>
  );
};

const BookNowModal = ({
  bookingRequestFormUrl,
  bookingStatus,
  hasGAP = false,
  isOpen = false,
  listing,
  moveIn = '',
  moveOut = '',
  onBookingDatesChange,
  onRequestClose,
  title,
  onOpenDatePickerEvent
}) => {
  const isUserLogged = useIsUserLogged();
  const { pricingDetails } = usePricingContext();

  const [isCalendarOpen, toggleCalendar] = useState(false);

  const handleLoginModalOpen = useCallback(() => {
    if (!isUserLogged) {
      triggerRedirectModal(bookingRequestFormUrl);
    }
  }, [bookingRequestFormUrl, isUserLogged]);

  const handleBookNowClickNoDates = useCallback(() => {
    toggleCalendar(true);
  }, []);

  const handleCalendarClose = useCallback(() => {
    toggleCalendar(false);
  }, []);

  const handleOpenDatePickerEvent = () => {
    onOpenDatePickerEvent();
    handleBookNowClickNoDates();
  };

  useEffect(() => {
    Mediator.subscribe(
      OPEN_BOOKNOW_DATEPICKER_MOBILE,
      handleOpenDatePickerEvent
    );

    return () =>
      Mediator.unsubscribe(
        OPEN_BOOKNOW_DATEPICKER_MOBILE,
        handleOpenDatePickerEvent
      );
  }, [handleBookNowClickNoDates]);

  const eligibleOffers = listing?.offers;
  const hasOffers = eligibleOffers?.length > 0;

  return (
    <Modal
      className="book-now-modal"
      contentLabel="modal"
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size="fullscreen"
    >
      <BookNowModalHeader onRequestClose={onRequestClose} title={title} />
      <ModalBody className="book-now-modal-body">
        <div
          className="book-now-modal-body__content"
          data-test="booknow-modal-body"
        >
          <div className="book-now-modal-body__content--spaced">
            <BookNowDatesInput
              className={classNames({
                'book-now-dates-input--with-offer-label': hasOffers
              })}
              availabilities={listing.availabilities}
              maxStay={listing.maxStay}
              minStay={listing.minStay}
              moveIn={moveIn}
              moveInDatesRanges={listing.moveInDatesRanges}
              contractDurationTypesAllowed={
                listing.contractDurationTypesAllowed
              }
              moveOut={moveOut}
              onChange={onBookingDatesChange}
              initiallyOpen={!moveIn || !moveOut}
              show={isCalendarOpen}
              onCalendarClose={handleCalendarClose}
              absolutePosition={isOpen ? moveIn || moveOut : true}
              cityId={listing.cityId}
            />
            <BookNowPermanentContractLabel
              allowsPermanentContract={listing.allowsPermanentContract}
              allowsTemporalContract={listing.allowsTemporalContract}
              countryCode={listing.countryCode}
            />
            {moveIn && moveOut ? (
              <OptimizeDates
                contractType={listing.contractType}
                moveIn={moveIn}
                moveOut={moveOut}
                onApplyOptimizedDates={onBookingDatesChange}
                listingId={listing.id}
              />
            ) : null}
            {pricingDetails.reservationPrice && (
              <>
                <BookNowPriceTable
                  reservationPrice={pricingDetails.reservationPrice}
                  bookingFee={pricingDetails.bookingFee}
                  cityId={listing.cityId}
                />
                <PaymentInfoModalButton
                  linkClassName="book-now-modal-payment-info"
                  linkText={trans('listing.book-now-card.payment-summary')}
                />
              </>
            )}
            <ValuePropositionFeatures
              listing={listing}
              dataTest="book-now-features-mobile"
            />
          </div>
        </div>
      </ModalBody>

      <div className="book-now-modal__footer-wrapper">
        <div
          className="book-now-modal__footer"
          data-test="booknow-modal-footer"
        >
          <BookNowModalPriceInfo />
          <span className="book-now-modal-booking-buttons">
            <BookNowButton
              bookingStatus={bookingStatus}
              hasGAP={hasGAP}
              href={bookingRequestFormUrl}
              instantBooking={listing.instantBooking}
              isLogged={isUserLogged}
              maxStay={listing.maxStay}
              minStay={listing.minStay}
              moveIn={moveIn}
              moveInDatesRanges={listing.moveInDatesRanges}
              allowsPermanentContract={listing.allowsPermanentContract}
              moveOut={moveOut}
              onClickBookNowButton={handleBookNowClickNoDates}
              onClickSuccessBookNowButton={handleLoginModalOpen}
              rentableUnitId={listing.id}
              unavailabilities={listing.unavailabilities}
              listing={listing}
              pricing={pricingDetails}
            />
          </span>
        </div>
        <div className="book-now-modal__pay-nothing-now">
          {trans('listing.book-now-card.payment-safety')}
        </div>
      </div>
    </Modal>
  );
};

BookNowModal.propTypes = {
  bookingRequestFormUrl: PropTypes.string.isRequired,
  bookingStatus: PropTypes.string.isRequired,
  listing: PropTypes.shape(ListingShape).isRequired,
  onBookingDatesChange: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  hasGAP: PropTypes.bool,
  isOpen: PropTypes.bool,
  moveIn: PropTypes.string,
  moveOut: PropTypes.string,
  onOpenDatePickerEvent: PropTypes.func.isRequired,
  cityId: PropTypes.string.isRequired
};

export default BookNowModal;
